/**
 * Persisted view state for the delegate schedule lists (Manage Draws, Live
 * Scoring, Prize Management): which rodeo cards are expanded, and which
 * Upcoming/Past tab is selected.
 *
 * All three pages want the same thing — you leave the page (or refresh) and
 * come back to the rows you had open, on the tab you were on, rather than a
 * collapsed list snapped back to Upcoming. Draws and Live Scoring each grew
 * their own copy of this; this is that code, once, so a third page doesn't make
 * a third copy.
 *
 * WHY sessionStorage, not localStorage: the memory is scoped to the browser tab.
 * It survives a refresh and survives navigating away and back (which is what
 * unmounts these lists — drilling into an event's scoring or draw view is a
 * route change), but it does NOT follow you into a new tab or persist for weeks.
 * An expansion set is transient working state, not a preference.
 *
 * Every accessor is failure-tolerant: sessionStorage can be unavailable
 * (disabled, quota, SSR) and losing the memory must degrade to "collapsed,
 * Upcoming", never to a thrown error that takes the page down with it.
 */

import type { ScheduleTab } from '@/utils/scheduleTabs';

export interface ScheduleListState {
  loadExpandedIds: () => Set<string>;
  saveExpandedIds: (ids: Set<string>) => void;
  loadTab: () => ScheduleTab;
  saveTab: (tab: ScheduleTab) => void;
}

/**
 * @param namespace Page identifier, e.g. 'delegate-prize-management'. Keys are
 *   namespaced so the three lists remember their own state independently — an
 *   admin expanding a rodeo on Draws should not silently expand it on Prize
 *   Management.
 */
export function createScheduleListState(namespace: string): ScheduleListState {
  const expandedKey = `highcall:${namespace}:expanded-rodeos`;
  const tabKey = `highcall:${namespace}:schedule-tab`;

  return {
    loadExpandedIds() {
      if (typeof window === 'undefined') return new Set();
      try {
        const raw = window.sessionStorage.getItem(expandedKey);
        if (!raw) return new Set();
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed)
          ? new Set(parsed.filter((x): x is string => typeof x === 'string'))
          : new Set();
      } catch {
        return new Set();
      }
    },

    saveExpandedIds(ids: Set<string>) {
      if (typeof window === 'undefined') return;
      try {
        window.sessionStorage.setItem(expandedKey, JSON.stringify([...ids]));
      } catch {
        // Unavailable (quota / disabled). Losing the memory is acceptable;
        // throwing here would not be.
      }
    },

    loadTab() {
      if (typeof window === 'undefined') return 'upcoming';
      try {
        const raw = window.sessionStorage.getItem(tabKey);
        return raw === 'past' || raw === 'upcoming' ? raw : 'upcoming';
      } catch {
        return 'upcoming';
      }
    },

    saveTab(tab: ScheduleTab) {
      if (typeof window === 'undefined') return;
      try {
        window.sessionStorage.setItem(tabKey, tab);
      } catch {
        // Same as above — best-effort.
      }
    },
  };
}
